import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { specialtiesService } from '../../services/specialtiesService';
import { Specialty } from '../../types';
import './DimensionsCatalogPage.css';

// Usamos import estático para asegurar que la imagen resuelva en el bundle
import florImg from '../../assets/images/flor.png';

const normalize = (value: string) => 
  String(value || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"").replace(/[^a-z0-9\s]/g," ").replace(/\s+/g," ").trim();

const dimOrder = ["cuerpo", "mente", "energia", "conciencia"] as const;
type DimKey = typeof dimOrder[number];

const dimInfo: Record<DimKey, any> = {
  cuerpo: { color: "var(--cuerpo)", soft: "rgba(36,46,123,.09)", border: "rgba(36,46,123,.24)", ink: "#242E7B" },
  mente: { color: "var(--mente)", soft: "rgba(93,138,186,.11)", border: "rgba(93,138,186,.28)", ink: "#4F769F" },
  energia: { color: "var(--energia)", soft: "rgba(83,169,110,.11)", border: "rgba(83,169,110,.28)", ink: "#3F8355" },
  conciencia: { color: "var(--conciencia)", soft: "rgba(87,64,147,.10)", border: "rgba(87,64,147,.26)", ink: "#574093" }
};

export const DimensionsCatalogPage: React.FC = () => {
  const { t } = useTranslation();
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [activeDim, setActiveDim] = useState<DimKey | 'todas'>('todas');
  const [searchQuery, setSearchQuery] = useState('');
  const [openDetailsId, setOpenDetailsId] = useState<string | null>(null);

  useEffect(() => {
    const fetchSpecialties = async () => {
      setLoading(true);
      const response = await specialtiesService.getSpecialties();
      if (response.success && response.data) {
        setSpecialties(response.data);
      }
      setLoading(false);
    };
    fetchSpecialties();
  }, []);

  const counts = useMemo(() => {
    const obj: Record<string, number> = { cuerpo: 0, mente: 0, energia: 0, conciencia: 0 };
    specialties.forEach(item => {
      const d = item.dimension?.slug?.toLowerCase();
      if (d && obj[d] !== undefined) {
        obj[d]++;
      }
    });
    return obj;
  }, [specialties]);

  const searchable = (item: Specialty) => {
    return normalize([item.name, item.description, ...(item.alias || []), ...(item.cover_wards || [])].join(" "));
  };

  const filteredSpecialties = useMemo(() => {
    const q = normalize(searchQuery);
    return specialties.filter(item => {
      const itemDim = item.dimension?.slug?.toLowerCase() as DimKey;
      const dimMatch = activeDim === 'todas' || itemDim === activeDim;
      const queryMatch = !q || searchable(item).includes(q);
      return dimMatch && queryMatch;
    });
  }, [specialties, activeDim, searchQuery]);

  const toggleDetails = (uid: string, event: React.SyntheticEvent<HTMLDetailsElement>) => {
    const isOpen = (event.target as HTMLDetailsElement).open;
    if (isOpen) {
      setOpenDetailsId(uid);
    } else if (openDetailsId === uid) {
      setOpenDetailsId(null);
    }
  };

  return (
    <main className="dimensions-page">
      <section className="page-hero">
        <div className="page-container">
          <div className="hero-card">
            <div>
              <p className="eyebrow">{t('DimensionsCatalogPage.hero.eyebrow')}</p>
              <h1>{t('DimensionsCatalogPage.hero.title')}</h1>
              <p className="hero-lead">{t('DimensionsCatalogPage.hero.lead')}</p>
              <div className="hero-actions">
                <a className="dim-btn-primary" href="#catalogo">{t('DimensionsCatalogPage.hero.btnPrimary')}</a>
                <a className="dim-btn-secondary" href="#dimensiones">{t('DimensionsCatalogPage.hero.btnSecondary')}</a>
              </div>
            </div>
            <aside className="hero-aside" aria-label="Flor y criterio de organización">
              <figure className="hero-flower">
                <img src={florImg} alt="Flor de Bodhiwell que integra las cuatro dimensiones" />
                <figcaption>{t('DimensionsCatalogPage.hero.figcaption')}</figcaption>
              </figure>
              <div className="hero-stat">
                <span className="catalog-total">{specialties.length}</span>
                <strong>{t('DimensionsCatalogPage.hero.statStrong')}</strong>
                <p>{t('DimensionsCatalogPage.hero.statP')}</p>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="page-section" id="dimensiones" aria-labelledby="dimensionsTitle">
        <div className="page-container">
          <div className="section-heading">
            <p className="eyebrow">{t('DimensionsCatalogPage.doors.eyebrow')}</p>
            <h2 id="dimensionsTitle">{t('DimensionsCatalogPage.doors.title')}</h2>
            <p className="section-copy">{t('DimensionsCatalogPage.doors.copy')}</p>
          </div>
          
          <div className="dimension-grid">
            {dimOrder.map(dim => {
              const info = dimInfo[dim];
              const style = {
                '--accent': info.color,
                '--accent-soft': info.soft,
                '--accent-border': info.border,
                '--accent-ink': info.ink,
              } as React.CSSProperties;

              return (
                <article key={dim} className="dimension-card" style={style}>
                  <span className="dimension-count">{counts[dim]} {t('DimensionsCatalogPage.doors.categoriesCount')}</span>
                  <h3>{t(`DimensionsCatalogPage.dimensions.${dim}.label`)}</h3>
                  <p>{t(`DimensionsCatalogPage.dimensions.${dim}.description`)}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="page-section catalog-section" id="catalogo" aria-labelledby="catalogTitle">
        <div className="page-container">
          <div className="section-heading">
            <p className="eyebrow">{t('DimensionsCatalogPage.catalog.eyebrow')}</p>
            <h2 id="catalogTitle">{t('DimensionsCatalogPage.catalog.title')}</h2>
            <p className="section-copy">{t('DimensionsCatalogPage.catalog.copy')}</p>
          </div>
          
          <div className="catalog-shell">
            <div className="toolbar">
              <div className="search-wrap">
                <input 
                  className="search" 
                  type="search" 
                  placeholder={t('DimensionsCatalogPage.catalog.searchPlaceholder')} 
                  autoComplete="off" 
                  aria-label={t('DimensionsCatalogPage.catalog.searchAria')}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="filters" aria-label={t('DimensionsCatalogPage.catalog.filterAria')}>
                <button 
                  className={`filter ${activeDim === 'todas' ? 'is-active' : ''}`}
                  type="button" 
                  data-dim="todas"
                  aria-pressed={activeDim === 'todas'}
                  onClick={() => setActiveDim('todas')}
                >
                  {t('DimensionsCatalogPage.catalog.btnAll')}
                </button>
                {dimOrder.map(dim => (
                  <button 
                    key={dim}
                    className={`filter ${activeDim === dim ? 'is-active' : ''}`}
                    type="button" 
                    data-dim={dim}
                    aria-pressed={activeDim === dim}
                    onClick={() => setActiveDim(dim)}
                  >
                    {t(`DimensionsCatalogPage.dimensions.${dim}.label`)}
                  </button>
                ))}
              </div>
            </div>
            
            <p className="verification-note" id="verificacion-documental">
              <strong>✓ {t('DimensionsCatalogPage.catalog.verificationStrong', 'Verificación documental Bodhiwell.')}</strong> {t('DimensionsCatalogPage.catalog.verificationText', 'Bodhiwell verifica la documentación presentada, sin que ello constituya un peritaje académico o profesional ni una habilitación para ejercer; el alcance de la práctica y el cumplimiento de los requisitos aplicables son responsabilidad de quien ofrece el servicio.')}
            </p>

            <p className="catalog-status" aria-live="polite">
              {filteredSpecialties.length === specialties.length 
                ? t('DimensionsCatalogPage.catalog.statusAll', { count: filteredSpecialties.length }) 
                : filteredSpecialties.length === 1 
                  ? t('DimensionsCatalogPage.catalog.statusOne', { count: filteredSpecialties.length }) 
                  : t('DimensionsCatalogPage.catalog.statusMany', { count: filteredSpecialties.length })
              }
            </p>

            <div id="catalog">
              {dimOrder.map(dim => {
                const group = filteredSpecialties.filter(item => item.dimension?.slug?.toLowerCase() === dim);
                if (group.length === 0) return null;

                const info = dimInfo[dim];
                const style = {
                  '--accent': info.color,
                  '--accent-soft': info.soft,
                  '--accent-border': info.border,
                  '--accent-ink': info.ink,
                } as React.CSSProperties;

                return (
                  <section key={dim} className="dimension-section" style={style}>
                    <h3 className="dimension-title"><span className="dimension-dot"></span>{t(`DimensionsCatalogPage.dimensions.${dim}.label`)}</h3>
                    <div className="category-list">
                      {group.map(item => {
                        const isOpen = openDetailsId === item.uid;
                        return (
                          <details 
                            key={item.uid} 
                            className="category" 
                            open={isOpen}
                            onToggle={(e) => toggleDetails(item.uid, e)}
                          >
                            <summary>
                              <div className="category-name">{item.name}</div>
                              <span className="plus" aria-hidden="true">+</span>
                            </summary>
                            <div className="category-body">
                              {item.description && (
                                <div dangerouslySetInnerHTML={{ __html: item.description }} />
                              )}
                              {item.alias && item.alias.length > 0 && (
                                <p className="related"><strong>{t('DimensionsCatalogPage.catalog.alsoSearched')}</strong> {item.alias.join(', ')}</p>
                              )}
                              {item.cover_wards && item.cover_wards.length > 0 && (
                                <p className="related"><strong>{t('DimensionsCatalogPage.catalog.relatedTopics')}</strong> {item.cover_wards.join(', ')}</p>
                              )}
                            </div>
                          </details>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>

            {filteredSpecialties.length === 0 && !loading && (
              <div className="empty" style={{ display: 'block' }}>
                {t('DimensionsCatalogPage.catalog.emptySearch')}
              </div>
            )}
            
            {loading && (
              <div className="empty" style={{ display: 'block' }}>
                {t('DimensionsCatalogPage.catalog.loading')}
              </div>
            )}

            <div className="catalog-contact" id="profesion-no-figura">
              <p className="catalog-contact-copy">
                <strong>{t('DimensionsCatalogPage.catalog.notFoundStrong', '¿Tu profesión o práctica no figura en Bodhiwell?')}</strong> {t('DimensionsCatalogPage.catalog.notFoundText', 'Si contás con formación acreditable y te gustaría ser parte de la comunidad, contactanos para que podamos conocer tu recorrido y evaluar su incorporación a Bodhiwell.')}
              </p>
              <a className="dim-btn-secondary" href="mailto:info@bodhiwell.uy">{t('DimensionsCatalogPage.catalog.contactBtn', 'Contactarnos')}</a>
            </div>

            <div className="legal-note">
              <strong>{t('DimensionsCatalogPage.catalog.important')}</strong> {t('DimensionsCatalogPage.catalog.legalNote')}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
